import { DateTime } from 'luxon';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useContextSelector } from 'use-context-selector';

import { DetailedViewSelector } from '@/core/store/detailed-view/detailed-view-states';
import { rootElementSelector } from '@/core/store/global/app-states';
import { viewEditModeSelector } from '@/core/store/global/app-view-states';
import { AppContext, setAppState } from '@/core/store/global/AppProvider';
import { CountPickerDropdown } from '@/ux-legacy/uikit/components/components-index';
import { DynamicForm } from '@/ux-legacy/uikit/elements/elements-index';

const userInputRules = {
  validate: {
    required: (item: any) => {
      return item?.value != undefined;
    },
  },
};

export type OptionsFormProps = {
  // itemData: any;
  assetManagerData: any;
  imageUploadElement?: React.RefObject<HTMLInputElement>;
  selectedImage?: any;
  onImageChange?: (event: ChangeEvent) => void;
  setPrice?: any;
  setVariant?: any;
  setDuration?: any;
  setPeopleQty?: any;
  setPeopleCount?: any;
  setQuantity?: any;
  align?: string;
  setMessageError?: any;
  hooksForm: any;
  hooksQtyForm: any;
  maxCapacity?: number;
  originalPrice?: any;
  formAction?: FormAction;
};

export type FormAction = 'ON_LOAD' | 'ON_SUBMIT';

const OptionsForm: FC<OptionsFormProps> = ({
  // itemData = null,
  assetManagerData = null,
  imageUploadElement,
  selectedImage,
  onImageChange,
  setPrice,
  setVariant,
  setDuration,
  setPeopleCount,
  setQuantity,
  align = 'vertical',
  setMessageError,
  hooksForm,
  hooksQtyForm,
  maxCapacity,
  originalPrice,
  formAction,
}) => {
  const dispatch = useContextSelector(AppContext, setAppState);
  const detailedView = useContextSelector(AppContext, DetailedViewSelector);
  const rootElement = useContextSelector(AppContext, rootElementSelector);
  const inEditMode = useContextSelector(AppContext, viewEditModeSelector);
  const [showImageGallery, setShowImageGallery] = useState(false);

  const [formInputs, setFormInputs] = useState<any[]>([]);

  const [formQtyInputs, setFormQtyInputs] = useState<any>([]);

  const [variantInfo, setVariantInfo] = useState<any>(null);

  const [bookingError, setError] = useState<string | null>(null);

  const [startDate, setStartDate] = useState(null);

  const [endDate, setEndDate] = useState(null);

  const [CountOutput, setCountOutput] = useState({ Adult: 0, Child: 0, Quantity: 0 });

  const [save, setSave] = useState({
    isSaving: false,
    isSaved: false,
  });

  useEffect(() => {
    resetForm();
  }, [assetManagerData]);

  useEffect(() => {
    if (formAction == 'ON_SUBMIT') {
      resetForm();
    }
  }, [formAction]);

  const resetForm = () => {
    setStartDate(assetManagerData?.product?.startDate);
    setEndDate(assetManagerData?.product?.endDate);

    if (assetManagerData?.product?.duration?.length == 1) {
      setDuration(assetManagerData?.product?.duration[0]?.value);
      setVariantInfo(assetManagerData?.product?.versions[0]);
    }

    const variantTitles: any[] = [];
    let duration = '';

    if (assetManagerData?.product?.versions?.length == 1) {
      setVariantInfo(assetManagerData?.product?.versions[0]);
      setVariant(assetManagerData?.product?.versions[0]);

      return;
    }

    if (assetManagerData?.product?.duration?.[0]?.value) {
      variantTitles.push(assetManagerData?.product?.duration?.[0]?.value);
      duration = assetManagerData?.product?.duration[0]?.values;
    }

    if (assetManagerData?.product?.option1?.values?.[0]) {
      variantTitles.push(assetManagerData?.product?.option1?.values?.[0]);
    }

    if (assetManagerData?.product?.option2?.values?.[0]) {
      variantTitles.push(assetManagerData?.product?.option2?.values?.[0]);
    }

    if (assetManagerData?.product?.option3?.values?.[0]) {
      variantTitles.push(assetManagerData?.product?.option3?.values?.[0]);
    }

    const variantTitleString = variantTitles?.join('/');
    const variant = getVariantsInfo(variantTitleString);

    if (variant) {
      setVariantInfo(variant);
      setVariant(variant);
    }

    setDuration?.(duration);

    hooksForm.reset({
      duration: {
        label: assetManagerData?.product?.duration ? assetManagerData?.product?.duration[0]?.label : '',
        value: assetManagerData?.product?.duration ? assetManagerData?.product?.duration[0]?.value : '',
      },
      option1: {
        label: assetManagerData?.product?.option1?.values[0],
        value: assetManagerData?.product?.option1?.values[0],
      },
      option2: {
        label: assetManagerData?.product?.option2?.values[0],
        value: assetManagerData?.product?.option2?.values[0],
      },
      option3: {
        label: assetManagerData?.product?.option3?.values[0],
        value: assetManagerData?.product?.option3?.values[0],
      },
    });
  };

  const generateFormFields = () => {
    // if (shopifyData && shopifyData?.product?.variants?.length > 1) {
    const formFields: any[] = [];

    if (assetManagerData?.product?.duration && assetManagerData?.product?.duration?.length > 1) {
      const items = assetManagerData?.product?.duration?.map((option: any) => {
        return { label: option.label, value: option.value };
      });
      formFields.push({
        name: 'duration',
        type: 'dropdown',
        label: 'Duration',
        rules: userInputRules,
        options: items || [],
      });
    }

    if (assetManagerData?.product?.option1 && assetManagerData?.product?.option1?.values?.length > 1) {
      const items = assetManagerData?.product?.option1.values.map((value: any) => {
        return { label: value, value };
      });
      formFields.push({
        name: 'option1',
        type: 'dropdown',
        label: assetManagerData?.product?.option1?.label,
        rules: userInputRules,
        options: items || [],
      });
    }

    if (assetManagerData?.product?.option2 && assetManagerData?.product?.option2?.values?.length > 1) {
      const items = assetManagerData?.product?.option2.values.map((value: any) => {
        return { label: value, value };
      });
      formFields.push({
        name: 'option2',
        type: 'dropdown',
        label: assetManagerData?.product?.option2?.label,
        rules: userInputRules,
        options: items || [],
      });
    }

    if (assetManagerData?.product?.option3 && assetManagerData?.product?.option3?.values?.length > 1) {
      const items = assetManagerData?.product?.option3.values.map((value: any) => {
        return { label: value, value };
      });
      formFields.push({
        name: 'option3',
        type: 'dropdown',
        label: assetManagerData?.product?.option3?.label,
        rules: userInputRules,
        options: items || [],
      });
    }

    setFormInputs(formFields);
  };

  const calculatePrice = (variantInfo: any) => {
    if (variantInfo) {
      // const formValues = hooksQtyForm.getValues();
      const formValues = CountOutput;
      let price = 0;

      if (variantInfo?.price?.type == 1 && formValues?.Quantity) {
        // Check price type  1. fixed 2. no of adults/children
        price = variantInfo?.price?.fixedPrice * formValues?.Quantity;
      } else if (variantInfo?.price?.type == 1 && !variantInfo?.quantity) {
        price = variantInfo?.price?.fixedPrice;
        // setQuantity(1);
        setCountOutput({ Adult: 0, Child: 0, Quantity: 1 });
      } else {
        if (formValues?.Adult) {
          price = variantInfo?.price?.adultPrice * formValues?.Adult;
        }

        if (formValues?.Child) {
          price += variantInfo?.price?.childPrice * formValues?.Child;
        }
      }
      if (assetManagerData?.product?.discountPercentage) {
        const discountAmount = (price * assetManagerData?.product?.discountPercentage) / 100;
        const discountedPrice = price - discountAmount;
        originalPrice(parseFloat(price.toString()).toFixed(2));
        setPrice(parseFloat(discountedPrice.toString()).toFixed(2));
      } else {
        setPrice(parseFloat(price.toString()).toFixed(2));
      }
      // dispatch(
      //   setDetailedViewAction({
      //     data: {
      //       ...detailedView.data,
      //       product: { ...detailedView?.data?.product, totalprice: price },
      //     },
      //   }),
      // );
    }
  };

  useEffect(() => {
    renderQtyFields();

    if (variantInfo) {
      setStartDate(variantInfo?.startDate);
      setEndDate(variantInfo?.endDate);
    } else {
      setStartDate(assetManagerData?.product?.startDate);
      setEndDate(assetManagerData?.product?.endDate);
    }

    // if (!variantInfo?.price || (!variantInfo?.noOfAdults && !variantInfo?.noOfChildren)) {
    // setPrice(null);
    // hooksQtyForm.reset();
    // }

    calculatePrice(variantInfo);
  }, [variantInfo, maxCapacity]);

  const fillQty = (minimum: number, limit: 10) => {
    const qtyArray = [];

    for (let i = minimum; i <= limit; i++) {
      qtyArray.push({ label: `${i}`, value: i });
    }

    return qtyArray;
  };

  const checkPersonCountIsOne = () => {
    let personCountExist = true;
    let childrenCountExist = true;
    if (variantInfo?.noOfAdults) {
      if (
        variantInfo?.noOfAdults?.minValue == 1 &&
        (maxCapacity == 1 || variantInfo?.noOfAdults?.maxValue == 1)
      ) {
        personCountExist = false;
      } else {
        personCountExist = true;
      }
    }

    if (variantInfo?.noOfChildren) {
      if (
        variantInfo?.noOfChildren?.minValue == 1 &&
        (maxCapacity == 1 || variantInfo?.noOfChildren?.maxValue == 1)
      ) {
        childrenCountExist = false;
      } else {
        childrenCountExist = true;
      }
    }

    return personCountExist && childrenCountExist;
  };

  const renderQtyFields = () => {
    const formFields: any[] = [];
    let initialValue: any = {};
    // if (variantInfo?.noOfAdults) {
    //   formFields.push({
    //     name: 'noOfAdults',
    //     type: 'dropdown',
    //     label: variantInfo?.noOfAdults?.label,
    //     options: fillQty(variantInfo?.noOfAdults?.minValue, variantInfo?.remainingCapacity || variantInfo?.totalCapacity || variantInfo?.noOfAdults?.maxValue),
    //     rules: userInputRules,
    //   })
    // }

    // if (variantInfo?.noOfChildren) {
    //   formFields.push({
    //     name: 'noOfChildren',
    //     type: 'dropdown',
    //     label: variantInfo?.noOfChildren?.label,
    //     options: fillQty(variantInfo?.noOfChildren?.minValue, variantInfo?.remainingCapacity || variantInfo?.totalCapacity || variantInfo?.noOfChildren?.maxValue),
    //     rules: userInputRules,
    //   })
    // }

    // if (variantInfo?.quantity) {
    //   formFields.push({
    //     name: 'quantity',
    //     type: 'dropdown',
    //     label: variantInfo?.quantity?.label,
    //     options: fillQty(variantInfo?.quantity?.minValue, variantInfo?.remainingCapacity || variantInfo?.totalCapacity || variantInfo?.quantity?.maxValue),
    //     rules: userInputRules,
    //   })
    // }

    if (variantInfo?.noOfAdults) {
      formFields.push({
        title: variantInfo?.noOfAdults?.label,
        defaultValue: variantInfo?.noOfAdults?.defaultValue || 1,
        min: variantInfo?.noOfAdults?.minValue,
        max:
          maxCapacity ||
          variantInfo?.remainingCapacity ||
          variantInfo?.totalCapacity ||
          variantInfo?.noOfAdults?.maxValue,
        optionName: 'Adult',
      });
      initialValue = {
        Adult: variantInfo?.noOfAdults?.defaultValue || 1,
      };
    }
    if (variantInfo?.noOfChildren) {
      formFields.push({
        title: variantInfo?.noOfChildren?.label,
        defaultValue: variantInfo?.noOfChildren?.defaultValue || 1,
        min: variantInfo?.noOfChildren?.minValue,
        max:
          maxCapacity ||
          variantInfo?.remainingCapacity ||
          variantInfo?.totalCapacity ||
          variantInfo?.noOfChildren?.maxValue,
        optionName: 'Child',
      });
      initialValue = { ...initialValue, Child: variantInfo?.noOfChildren?.defaultValue || 1 };
    }
    if (variantInfo?.quantity) {
      formFields.push({
        title: variantInfo?.quantity?.label,
        defaultValue: 1,
        min: variantInfo?.quantity?.minValue,
        max:
          maxCapacity ||
          variantInfo?.remainingCapacity ||
          variantInfo?.totalCapacity ||
          variantInfo?.quantity?.maxValue,
        optionName: 'Quantity',
      });
      initialValue = { ...initialValue, Quantity: 1 };
    }

    setCountOutput(initialValue);
    setFormQtyInputs(formFields);
  };

  useEffect(() => {
    const subscription = hooksForm.watch((value: any) => {
      const variantTitles: any[] = [];
      const startTime = {};
      let duration = '';

      const formFieldValue: any = hooksForm.getValues();

      if (hooksForm.getValues()?.duration?.value) {
        variantTitles.push(formFieldValue?.duration?.value);
        duration = formFieldValue?.duration?.value;
      }

      if (formFieldValue?.option1?.value) {
        variantTitles.push(formFieldValue?.option1?.value);
      }

      if (formFieldValue?.option2?.value) {
        variantTitles.push(formFieldValue?.option2?.value);
      }

      if (formFieldValue?.option3?.value) {
        variantTitles.push(formFieldValue?.option3?.value);
      }

      const variantTitleString = variantTitles?.join('/');
      const variant = getVariantsInfo(variantTitleString);

      if (variant) {
        setVariantInfo(variant);
        setVariant(variant);
      }

      setDuration?.(duration);
    });

    return () => subscription.unsubscribe();
  }, [hooksForm.watch, assetManagerData?.product]);

  useEffect(() => {
    const subscription = hooksQtyForm.watch((value: any) => {
      let peopleCount: any = {};
      const quantity = 0;

      calculatePrice(variantInfo);

      // for (const [key, value] of Object.entries(hooksQtyForm.getValues())) {
      //   const formFieldValue: any = value;

      //   if (key == 'noOfAdults' && formFieldValue?.value) {
      //     peopleCount['adults'] = formFieldValue?.value
      //   }

      //   if (key == 'noOfChildren' && formFieldValue?.value) {
      //     peopleCount['children'] = formFieldValue?.value
      //   }

      //   if (key == 'quantity' && formFieldValue?.value) {
      //     quantity = formFieldValue?.value;
      //     setQuantity(formFieldValue?.value);
      //   }
      // }

      peopleCount = {
        adults: CountOutput?.Adult,
        children: CountOutput?.Child,
      };
      setQuantity(CountOutput?.Quantity);

      setPeopleCount?.(peopleCount);

      if (
        variantInfo?.remainingCapacity <
        (quantity || 0) + (peopleCount?.adults || 0) + Number(peopleCount?.children || 0)
      ) {
        setMessageError('Sold Out and add to cart icon disabled');
      } else {
        setMessageError(null);
      }
    });

    hooksQtyForm.reset({
      noOfAdults: {
        label: variantInfo?.noOfAdults?.defaultValue?.toString(),
        value: variantInfo?.noOfAdults?.defaultValue,
      },
      noOfChildren: {
        label: variantInfo?.noOfChildren?.defaultValue?.toString(),
        value: variantInfo?.noOfChildren?.defaultValue,
      },
      quantity: {
        label: variantInfo?.quantity?.defaultValue?.toString(),
        value: variantInfo?.quantity?.defaultValue,
      },
    });

    return () => subscription.unsubscribe();
  }, [CountOutput, variantInfo?._id]);

  useEffect(() => {
    verifyVersionIsAvailable();
  }, [variantInfo]);

  const verifyVersionIsAvailable = () => {
    const optionVales = hooksForm.getValues();

    if (
      !variantInfo &&
      (optionVales?.option1 || optionVales?.option2 || optionVales?.option3 || optionVales?.duration)
    ) {
      setMessageError('Version not found, Please select other option');
    }

    if (
      variantInfo?.lastOrderDate &&
      DateTime.fromISO(variantInfo?.lastOrderDate).toUnixInteger() <
        DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toUnixInteger()
    ) {
      setMessageError('Last order date is over');
      setPrice(null);
      setFormQtyInputs(null);
    }
  };

  const getVariantsInfo = (title: string) => {
    return assetManagerData?.product?.versions?.find((item: any) => item?.title === title);
  };

  useEffect(() => {
    hooksForm.reset();
    generateFormFields();
  }, [assetManagerData]);

  // const onQuantityChange = (quantity: number) => {
  //   setCountOutput({ ...CountOutput, Quantity: quantity })
  // }

  return (
    <>
      <div className={align == 'vertical' ? 'inline' : 'flex'}>
        <DynamicForm
          userInputs={formInputs}
          hooksForm={hooksForm}
          classNames="flex-col flex-wrap m-2"
          fieldClassNames="my-1  w-auto"
          rootElement={rootElement}
          editEnabled={false}
        />
        {/* {formQtyInputs ? <div className=''>
        <DynamicForm
          userInputs={formQtyInputs}
          hooksForm={hooksQtyForm}
          classNames="flex-row flex-wrap"
          fieldClassNames="my-1 mx-1 w-auto"
          rootElement={rootElement}
          editEnabled={false}
        />
      </div> : null} */}

        {formQtyInputs.length > 0 && checkPersonCountIsOne() ? (
          <div className="my-1 mx-1 w-auto">
            <CountPickerDropdown
              title={'Participants'}
              fields={formQtyInputs}
              rootElement={rootElement}
              fieldClassNames="my-1 mx-1 w-auto md:max-w-[250px]"
              onSubmit={(values: any) => {
                setCountOutput(values);
              }}
            />
          </div>
        ) : null}
        {/* 
        {variantInfo?.price?.type == 1 && formQtyInputs.length <=0 && (
          <QuantityButton quantity={CountOutput.Quantity} onQuantityChange={onQuantityChange} />
        )} */}
      </div>
    </>
  );
};

export default OptionsForm;
